import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, IAuthProvider, ModelService } from 'core-client/api';
import { SharedModule } from '../../shared/shared.module';
import { Subject, takeUntil } from 'rxjs';

/**
 * Login component states
 */
enum LoginState {
  LOADING = 'LOADING',           // Initial loading
  READY = 'READY',               // Ready to show login options
  REDIRECTING = 'REDIRECTING',   // Redirecting to OAuth provider
  ERROR = 'ERROR'                // Generic error (providers unavailable)
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [SharedModule],
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // State management
  currentState: LoginState = LoginState.LOADING;

  // Auth providers
  activeAuthProviders: IAuthProvider[] = [];
  localProvider?: IAuthProvider;

  constructor(
    private readonly translator: TranslateService,
    private readonly authService: AuthService,
    private readonly modelService: ModelService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.translator.setDefaultLang('de');
    if (!this.translator.currentLang) {
      this.translator.use('de');
    }
  }

  async ngOnInit(): Promise<void> {
    await this.initializeAuthConfiguration();
  }

  ngAfterViewInit(): void {
    this.setupAuthenticationSubscriptions();
  }

  /**
   * Initialize authentication configuration with detailed error logging
   */
  private async initializeAuthConfiguration(): Promise<void> {
    try {
      console.log('[Login] Initializing authentication configuration...');

      // Attempt to load auth providers from backend
      await this.loadAuthProviders();

      // Check if we have any authentication methods
      const hasLocalProvider = this.localProvider?.enabled ?? false;
      const hasOAuthProviders = this.activeAuthProviders.length > 0;

      console.log('[Login] Providers loaded:', {
        hasLocalProvider,
        hasOAuthProviders,
        localProvider: this.localProvider?.display_name,
        oauthProviders: this.activeAuthProviders.map(p => p.display_name)
      });

      if (!hasLocalProvider && !hasOAuthProviders) {
        console.error('[Login] No authentication providers available');
        this.currentState = LoginState.ERROR;
        this.cdr.detectChanges(); // Trigger change detection
        return;
      }

      // Handle auto-redirect for single provider
      await this.handleSingleProviderAutoRedirect();

    } catch (error) {
      console.error('[Login] Failed to initialize authentication:', error);

      this.currentState = LoginState.ERROR;
      this.cdr.detectChanges(); // Trigger change detection
    }
  }

  /**
   * Load authentication providers from backend
   */
  private async loadAuthProviders(): Promise<void> {
    console.log('[Login] Loading authentication providers from backend...');

    const providers = await this.modelService._authProvider.listAll();

    console.log('[Login] Received providers:', providers);

    this.localProvider = providers.find((item: IAuthProvider) =>
      item.type === 'local'.toUpperCase()
    );

    this.activeAuthProviders = providers.filter((item: IAuthProvider) =>
      item.type !== 'local'.toUpperCase() && item.enabled
    );

    console.log('[Login] Parsed providers:', {
      localProvider: this.localProvider,
      activeAuthProviders: this.activeAuthProviders
    });
  }

  /**
   * Handle auto-redirect for single provider scenario
   */
  private async handleSingleProviderAutoRedirect(): Promise<void> {
    const hasOnlyLocalProvider = this.activeAuthProviders.length === 0 && this.localProvider?.enabled;
    const hasOnlyOAuthProvider = this.activeAuthProviders.length === 1 && !this.localProvider?.enabled;

    if (!hasOnlyLocalProvider && !hasOnlyOAuthProvider) {
      // Multiple providers - show selection
      console.log('[Login] Multiple providers detected, showing selection');
      this.currentState = LoginState.READY;
      this.cdr.detectChanges();
      return;
    }

    // Single provider detected
    const singleProvider = this.localProvider ?? this.activeAuthProviders[0];
    console.log('[Login] Single provider detected, initiating auto-redirect:', {
      provider: singleProvider.display_name,
      type: singleProvider.type
    });

    try {
      this.currentState = LoginState.REDIRECTING;
      this.cdr.detectChanges();

      // Configure the provider
      console.log('[Login] Setting auth config for provider:', singleProvider._id);
      await this.authService.setAuthConfig(singleProvider);

      console.log('[Login] Loading discovery document...');
      await this.authService.loadDiscoveryDocument();

      // Check if already authenticated
      if (this.authService.isAuthenticated()) {
        console.log('[Login] User already authenticated, navigation will be handled by auth service');
        return;
      }

      // Initiate OAuth login - redirects to provider
      console.log('[Login] Initiating OAuth login flow...');
      await this.authService.loginWithOAuth();

    } catch (error) {
      console.error('[Login] Single provider auto-redirect failed:', error);
      console.error('[Login] Provider details:', {
        providerId: singleProvider._id,
        providerName: singleProvider.display_name,
        providerType: singleProvider.type,
        error: error
      });
      this.currentState = LoginState.ERROR;
      this.cdr.detectChanges();
    }
  }

  /**
   * Setup authentication state subscriptions
   */
  private setupAuthenticationSubscriptions(): void {
    // Monitor authentication state
    this.authService.isAuthenticated$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((authenticated: boolean) => {
        if (authenticated) {
          console.log('[Login] User authenticated, navigation handled by auth service');
        }
      });

    // Monitor OAuth configuration errors
    this.authService.oauthConfigError$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasError: boolean) => {
        if (hasError && this.currentState === LoginState.LOADING) {
          console.error('[Login] OAuth configuration error detected');
          this.currentState = LoginState.ERROR;
          this.cdr.detectChanges();
        }
      });

    // Monitor loading completion
    this.authService.isDoneLoadingDocument$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((done: boolean) => {
        if (done && this.currentState === LoginState.LOADING) {
          console.log('[Login] Discovery document loading complete');
          // Loading complete, show login form
          if (!this.authService.isAuthenticated()) {
            this.currentState = LoginState.READY;
            this.cdr.detectChanges();
          }
        }
      });
  }

  /**
   * Login with selected provider
   */
  async loginWithProvider(provider: IAuthProvider): Promise<void> {
    if (this.currentState === LoginState.REDIRECTING) {
      return;
    }

    console.log('[Login] User selected provider:', {
      providerId: provider._id,
      providerName: provider.display_name,
      providerType: provider.type
    });

    this.currentState = LoginState.REDIRECTING;
    this.cdr.detectChanges();

    try {
      console.log('[Login] Setting auth config...');
      await this.authService.setAuthConfig(provider);

      console.log('[Login] Loading discovery document...');
      await this.authService.loadDiscoveryDocument();

      console.log('[Login] Initiating OAuth login...');
      await this.authService.loginWithOAuth();

    } catch (error) {
      console.error('[Login] Provider login failed:', error);
      console.error('[Login] Provider details:', {
        providerId: provider._id,
        providerName: provider.display_name,
        providerType: provider.type,
        config: provider.config,
        error: error
      });
      this.currentState = LoginState.ERROR;
      this.cdr.detectChanges();
    }
  }

  /**
   * Retry initialization after error
   */
  async retryInitialization(): Promise<void> {
    console.log('[Login] User initiated retry');
    this.currentState = LoginState.LOADING;
    this.cdr.detectChanges();
    await this.initializeAuthConfiguration();
  }

  /**
   * Template getters for cleaner template logic
   */
  get isLoading(): boolean {
    return this.currentState === LoginState.LOADING;
  }

  get isRedirecting(): boolean {
    return this.currentState === LoginState.REDIRECTING;
  }

  get isReady(): boolean {
    return this.currentState === LoginState.READY;
  }

  get hasError(): boolean {
    return this.currentState === LoginState.ERROR;
  }

  get showLocalProvider(): boolean {
    return this.isReady && (this.localProvider?.enabled ?? false);
  }

  get showOAuthProviders(): boolean {
    return this.isReady && this.activeAuthProviders.length > 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
